<template>
  <section class="all-store-list">
    <section class="section-block">
      <SectionTitle :title="displayText.title" hideBtn />
      <FilterContainer>
        <BaseElInput
          v-model="search.name"
          class="mt-[20px]"
          :placeholder="displayText.name.placeholder"
          clearable
          @clear="refresh(true)"
          @keypress.enter.native="refresh(true)"
        >
          <i
            slot="suffix"
            class="el-input__icon el-icon-search"
            @click="refresh(true)"
          />
        </BaseElInput>
      </FilterContainer>
      <BaseTable v-loading="loading.table" :data="displayData" :empty-text="displayText.table.noData">
        <EmptyBlock slot="empty" />
        <BaseElTableColumn :prop="'name'" :label="displayText.table.store" align="center" />
        <BaseElTableColumn :prop="'type'" :label="displayText.table.businessTime" align="center">
          <template slot-scope="scope">
            <p class="text-sub">{{ scope.row.type }}</p>
          </template>
        </BaseElTableColumn>
        <BaseElTableColumn :prop="'excludedDays'" :label="displayText.table.excludedDays" align="center">
          <template slot-scope="scope">
            <Tag :type="scope.row.excludedDays ? 'action' : 'info'">
              {{ scope.row.excludedDays ? displayText.tag.enabled : displayText.tag.disabled }}
            </Tag>
          </template>
        </BaseElTableColumn>
        <BaseElTableColumn :label="displayText.table.action" align="center">
          <template slot-scope="scope">
            <TableEditBtnGroup
              hideDelete
              @edit="onEdit(scope.row)"
            />
          </template>
        </BaseElTableColumn>
      </BaseTable>
    </section>
    <AllStoreListDialog
      v-if="showDialog"
      :formRules="formRules"
      :data="formData"
      :selectRow="selectRow"
      :storeBusinessTimeTypeConfig="storeBusinessTimeTypeConfig"
      @close="showDialog = false"
      @confirm="confirm"
    />
  </section>
</template>

<script>
import { defineComponent, ref, onMounted, reactive, computed } from 'vue'
import FilterContainer from '@/components/Container/FiltersContainer.vue'
import AllStoreListDialog from './AllStoreListDialog.vue'
import { useTable } from '@/use/table'
import { GetBranchStoreBusinessTime, FindBranchStoreBusinessTime, UpdateBranchStoreBusinessTime } from '@/api/branchStore'
import { map, get, find, filter, isNull } from 'lodash'
import { storeBusinessTimeTypeConfig } from '@/config/reservation'
import { noEmptyWeekRangeInterval, noEmptyRules } from '@/validation'
import { useBusinessTime } from '@/use/useBusinessTime'

export default defineComponent({
  name: 'AllStoreList',
  components: { FilterContainer, AllStoreListDialog },
  setup () {
    const search = reactive({
      name: null,
    })
    const formData = reactive({
      BranchId: null,
      mode: null,
      customWeekConfigs: [],
      excludedDays: false,
      excludeTimeRules: [],
    })
    const selectRow = ref(null)
    const formRules = computed(() => {
      const rules = {
        customWeekConfigs: [noEmptyWeekRangeInterval()],
        excludeTimeRules: [noEmptyRules()],
      }
      return rules
    })
    const showDialog = ref(false)
    const {
      shopId,
      tableData,
      loading,
      fetchData,
    } = useTable()

    const {
      mergeTimeIntervals,
      timeToMinutes,
      minutesToTime,
      formatExcludeTime,
      syncShopExcludeTimeRules,
    } = useBusinessTime()

    const displayText = computed(() => {
      return {
        title: '門市營業時間',
        name: {
          placeholder: '請輸入門市',
        },
        table: {
          noData: '暫無數據',
          store: '門市',
          businessTime: '營業時間',
          excludedDays: '門市排除特定時段',
          action: '操作',
        },
        tag: {
          enabled: '啟用',
          disabled: '未啟用',
        },
      }
    })

    const displayData = computed(() => {
      return filter(map(tableData.value, item => {
        const { name, id } = get(item, 'branch')
        const mode = get(item, 'branchBusinessTime.mode')
        const excludeTimeRules = get(item, 'branchBusinessTime.excludeTimeRules')
        const customWeekConfigs = get(item, 'branchBusinessTime.customWeekConfigs')
        return {
          name,
          id,
          mode,
          type: get(find(storeBusinessTimeTypeConfig, config => config.value === mode), 'label', mode),
          // excludeTimeRules: !isEmpty(excludeTimeRules),
          customWeekConfigs,
          excludedDays: !isNull(excludeTimeRules) && excludeTimeRules.length > 0,
          excludeTimeRules: excludeTimeRules ? syncShopExcludeTimeRules(excludeTimeRules) : [],
        }
      }), search.name ? item => item.name.toLowerCase().includes(search.name.toLowerCase()) : () => true)
    })

    const getTableData = async () => {
      const payload = {
        shopId: shopId.value,
      }
      await Promise.allSettled([
        fetchData(GetBranchStoreBusinessTime, payload),
      ])
    }
    const syncData = async (data) => {
      formData.BranchId = get(data, 'BranchId')
      formData.mode = get(data, 'mode')
      const customWeekConfigs = get(data, 'customWeekConfigs')
      const newWeeks = customWeekConfigs.map(item => {
        const { week, timeStart, timeEnd } = item
        return {
          week: week,
          timeStart: minutesToTime(timeStart),
          timeEnd: minutesToTime(timeEnd),
        }
      })
      formData.customWeekConfigs = newWeeks || [{ week: null, timeStart: null, timeEnd: null }]
      formData.excludeTimeRules = syncShopExcludeTimeRules(get(data, 'excludeTimeRules'))
      formData.excludedDays = !isNull(get(data, 'excludeTimeRules'))
    }
    const findBranchStoreBusinessTime = async (branchId) => {
      const payload = {
        shopId: shopId.value,
        branchId: branchId,
      }
      const [res, err] = await FindBranchStoreBusinessTime(payload)
      if (err) {
        window.$message.error(err)
        return
      }
      await syncData(res)
    }
    const updateBranchStoreBusinessTime = async (data) => {
      const customWeekConfigs = get(data, 'customWeekConfigs')
      const mode = get(data, 'mode')
      const excludeTimeRules = get(data, 'excludeTimeRules')
      const excludedDays = get(data, 'excludedDays')
      const newWeeks = []
      customWeekConfigs.forEach(item => {
        const { week, timeStart, timeEnd } = item
        newWeeks.push({
          week: week,
          timeStart: timeToMinutes(timeStart),
          timeEnd: timeToMinutes(timeEnd),
        })
      })
      const payload = {
        shopId: shopId.value,
        branchId: get(selectRow.value, 'id'),
        mode,
        customWeekConfigs: mergeTimeIntervals(newWeeks),
        excludeTimeRules: excludedDays ? formatExcludeTime(excludeTimeRules) : null,
      }
      const [, err] = await UpdateBranchStoreBusinessTime(payload)
      if (err) {
        window.$message.error(err)
        return
      }
      window.$message.success('更新成功')
      await refresh()
      showDialog.value = false
    }

    const refresh = async (search = false) => {
      loading.table = true
      await getTableData()
      loading.table = false
    }
    const onEdit = async (row) => {
      const { id } = row
      selectRow.value = row
      await findBranchStoreBusinessTime(id)
      showDialog.value = true
    }
    const confirm = async (data) => {
      await updateBranchStoreBusinessTime(data)
    }
    onMounted(() => {
      refresh()
    })
    return {
      search,
      tableData,
      loading,
      refresh,
      displayData,
      onEdit,
      showDialog,
      storeBusinessTimeTypeConfig,
      formData,
      formRules,
      confirm,
      displayText,
      selectRow,
    }
  },
})
</script>

<style scoped lang="postcss">
::v-deep .el-radio-group .el-radio__label {
  @apply text-sub
}
</style>
